import type { Coordinate } from './editor-feature';

/**
 * What the backend's automation reports.
 *
 * Mirrors the three slices behind `/floorplan/{id}/validation`, `/suggestions` and
 * `/clean-metadata`. Nothing here is applied by the server: the two that change a plan come
 * back as proposals and are applied through the editor's own document, so they land in the
 * undo stack and are written by the save path that already accounts for room capacity.
 */

export type IssueLevel = 'Error' | 'Warning' | 'Info';

/**
 * The severity, whatever shape it arrived in.
 *
 * The server sends a name. It has not always: an enum without an explicit converter
 * serialises as a number on this API, and a panel that called `.toLowerCase()` on a `2`
 * threw while rendering — which takes the whole check panel down rather than showing one
 * issue oddly. So the level is normalised on the way in and anything unrecognised is treated
 * as information: a severity nobody can name is not grounds for an alarm, and it is
 * certainly not grounds for a blank screen.
 */
export const levelOf = (level: unknown): IssueLevel => {
  if (typeof level === 'number') {
    // The enum's own order, worst last, as the server declares it.
    if (level >= 2) return 'Error';

    return level === 1 ? 'Warning' : 'Info';
  }

  const name = typeof level === 'string' ? level.toLowerCase() : '';

  if (name === 'error') return 'Error';
  if (name === 'warning') return 'Warning';

  return 'Info';
};

export interface FloorPlanIssue {
  readonly level: IssueLevel;
  /** The stored layer this concerns, or `FloorPlan` for the plan as a whole. */
  readonly layer: string;
  /**
   * What is wrong, as a code rather than a sentence.
   *
   * The server states the fact and the portal owns the wording, because the people reading
   * it work in English and Dutch and the server speaks neither on their behalf.
   */
  readonly code: string;
  readonly count: number;
  /** One affected feature's identifier, so a count of 40 is something to start looking at. */
  readonly example?: string;
}

export interface FloorPlanValidation {
  readonly floorPlanId: string;
  readonly storey: number;
  readonly issues: readonly FloorPlanIssue[];
  readonly errors: number;
  readonly warnings: number;
}

export interface DetectedDesk {
  readonly x: number;
  readonly y: number;
  readonly widthMetres: number;
  readonly depthMetres: number;
  readonly rotation: number;
  /** The room it is drawn inside, or null. A suggestion — a desk is bookable through its room. */
  readonly suggestedRoomId: string | null;
}

export interface SuggestedRoom {
  /** The outline, in the plan's own units, ready to be drawn as a zone. */
  readonly ring: readonly Coordinate[];
  readonly areaSquareMetres: number;
  readonly workplaceCount: number;
}

export interface WorkplaceRoomProposal {
  readonly workplaceId: string;
  readonly currentRoomId: string | null;
  readonly suggestedRoomId: string | null;
}

export interface FloorPlanSuggestions {
  readonly floorPlanId: string;
  /** Null when nobody has measured the plan, which is why the two size-based lists are empty. */
  readonly unitsPerMetre: number | null;
  readonly desks: readonly DetectedDesk[];
  readonly rooms: readonly SuggestedRoom[];
  readonly workplaceRooms: readonly WorkplaceRoomProposal[];
}

export interface CleaningLayerReport {
  readonly layer: string;
  readonly droppedEmpty: number;
  readonly closedRings: number;
  readonly addedIdentifiers: number;
  readonly strippedFields: number;
}

export interface CleaningReport {
  readonly floorPlanId: string;
  readonly dryRun: boolean;
  readonly changedAnything: boolean;
  readonly layers: readonly CleaningLayerReport[];
}

/** How many features one cleaning report touches in total, for the summary line. */
export const cleaningTotalOf = (report: CleaningReport): number => report.layers.reduce(
  (total, layer) => total + layer.droppedEmpty + layer.closedRings + layer.addedIdentifiers + layer.strippedFields,
  0,
);
